/**
 * The baked floor: tiles, rock outcrops, deposit markers, spawn-edge shimmer.
 * Rendered once — the ground never changes (doc 12).
 *
 * Doc 13 §F's stage-6 addition: **variance**. A 40×40 checkerboard of two
 * colours reads as graph paper at any zoom, so every floor tile draws one of
 * four treatments and every rock one of three silhouettes, chosen by a hash of
 * its own coordinates. No storage, no seed to thread, and the same tile always
 * looks the same — which is what makes it a place rather than a pattern.
 *
 * The variance is deliberately *below* the readability floor: the four floor
 * treatments differ by one shade step, so a building, a fish and a deposit all
 * still win the eye instantly (doc 10 §Legibility).
 */

import { TH, TW, tileCentre } from '../render/iso.ts';
import { groundRamp, type DepthPalette } from '../render/palette.ts';
import { isoDiamond, isoDiamondRim } from '../render/primitives.ts';
import { hex, PixelSurface, shade } from '../render/surface.ts';
import { T_ROCK, T_VEIN, T_VENT, type SimGrid } from '../sim/grid.ts';
import type { MapDef } from '../sim/types.ts';

export function bakeGround(grid: SimGrid, map: MapDef, depth: DepthPalette): PixelSurface {
  const size = map.size;
  const w = (size - 1) * TW * 2 + TW * 2;
  const h = (size - 1) * TH * 2 + TH * 4;
  const s = new PixelSurface(w, h);
  const g = groundRamp(depth);
  const origin = { ox: (size - 1) * TW + TW, oy: TH };
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      const p = tileCentre(i, j, origin);
      const t = grid.terrain[grid.idx(i, j)]!;
      const v = variant(i, j);
      if (t === T_ROCK) {
        // low dark outcrop: clearly not floor, clearly not yours. Three heights
        // so a reef bar is a ridge rather than an extruded rectangle.
        const lift = 2 + (v % 3);
        isoDiamond(s, p.x, p.y, TH, shade(depth.terrain, 0.42));
        isoDiamond(s, p.x, p.y - lift, TH, shade(depth.terrain, 0.62));
        if (v % 3 === 2) isoDiamond(s, p.x, p.y - lift - 2, TH - 4, shade(depth.terrain, 0.74));
      } else {
        isoDiamond(s, p.x, p.y, TH, (i + j) % 2 === 0 ? g.tileA : g.tileB);
        detail(s, p.x, p.y, v, g.tileA, g.tileB);
        if (t === T_VEIN) isoDiamondRim(s, p.x, p.y, TH, shade(hex('#93a6ab'), 0.8));
        if (t === T_VENT) isoDiamondRim(s, p.x, p.y, TH, shade(depth.playerGlow, 0.5));
      }
    }
  }
  // authored spawn edges shimmer faintly — the doors the ocean uses (doc 03)
  const edgeGlow = shade(depth.enemyGlow, 0.35);
  for (const edge of map.spawnEdges) {
    for (let a = 0; a < size; a++) {
      const [i, j] =
        edge === 'e' ? [size - 1, a] : edge === 'w' ? [0, a] : edge === 'n' ? [a, 0] : [a, size - 1];
      if (grid.terrain[grid.idx(i!, j!)] === T_ROCK) continue;
      const p = tileCentre(i!, j!, origin);
      isoDiamondRim(s, p.x, p.y, TH, edgeGlow, true, a % 2);
    }
  }
  return s;
}

/**
 * The two colours variance is allowed to add, so the readability test can name
 * them rather than guess which of the floor's colours were grain.
 */
export function grainColours(depth: DepthPalette): { grit: number; pale: number } {
  const g = groundRamp(depth);
  return { grit: shade(g.tileA, 0.86), pale: shade(g.tileB, 1.14) };
}

/** A stable 0..11 from a tile's own coordinates. No state, no seed, no storage. */
function variant(i: number, j: number): number {
  const h = Math.imul(i * 374761393 + j * 668265263, 1274126177) >>> 0;
  return (h >>> 20) % 12;
}

/**
 * One of four floor treatments, each a single shade step from the tile it sits
 * on: bare, scattered grit, a scour line, a pair of shells. Anything louder
 * than this competes with the things the player has to see.
 */
function detail(s: PixelSurface, cx: number, cy: number, v: number, a: number, b: number): void {
  const grit = shade(a, 0.86);
  const pale = shade(b, 1.14);
  switch (v % 4) {
    case 0:
      return; // bare floor, and a third of the seabed should be
    case 1:
      s.px(cx - 5, cy + 1, grit);
      s.px(cx + 3, cy - 2, grit);
      s.px(cx + 7, cy + 2, grit);
      break;
    case 2:
      for (let k = -6; k <= 6; k += 2) s.px(cx + k, cy + (k >> 2), grit);
      break;
    default:
      s.px(cx - 3, cy + 2, pale);
      s.px(cx - 2, cy + 2, pale);
      s.px(cx + 5, cy - 1, pale);
      break;
  }
}
